export function toMinutes(h) {
  if (h.includes(':')) {
    // Format "HH:mm:ss"
    const [hours, minutes] = h.split(':').map(Number);
    return hours * 60 + minutes;
  } else {
    // Format "Xh00"
    const [h1, m1] = h.split('h').map(Number);
    return h1 * 60 + (m1 || 0);
  }
};

export function toHourStr(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h${m.toString().padStart(2, '0')}`;
};

export function formattedTime(time) {
  const [hh, mm] = time.split(":");
  const formatted = `${hh.padStart(2, "0")}:${mm}`;
  return formatted;
}

export function calculateTopPx({ startHour, referenceHour = "8h00", pxPerHour = 100 }) {

  const startMin = toMinutes(startHour);
  const refMin = toMinutes(referenceHour);
  const diffMin = startMin - refMin;
  return diffMin * (pxPerHour / 60);
}

export function calculateHeightPx({ startHour, endHour, pxPerHour = 100 }) {
  const startMin = toMinutes(startHour);
  const endMin = toMinutes(endHour);
  const durationMin = endMin - startMin;
  return durationMin * (pxPerHour / 60);
}

export function isCourseInInterval(courses, startHour, endHour) {
  const startMin = toMinutes(startHour);
  const endMin = toMinutes(endHour);

  for (const course of courses) {
    const courseStartMin = toMinutes(course.startTime);
    const courseEndMin = toMinutes(course.endTime);

    if (startMin >= courseStartMin && endMin <= courseEndMin) {
      return true;
    }
  }

  return false;
}

export function getMinutesBetween(startHour, endHour) {
  const startMin = toMinutes(startHour);
  const endMin = toMinutes(endHour);

  return endMin - startMin;
}


/**
 * Renvoie les intervalles horaires sans cours pour un jour donné
 * @param {Array} coursesOfDay - Tableau de cours [{startTime, endTime, ...}]
 * @param {string} startDayHour - Heure de début de la journée ("8h00")
 * @param {string} endDayHour - Heure de fin de la journée ("19h00")
 * @returns {Array} - [{startHour, endHour}, ...]
 */
export function getEmptyIntervals(coursesOfDay, startDayHour, endDayHour) {

  // Trier les cours par heure de début
  const sorted = [...coursesOfDay].sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));
  const intervals = [];
  let prevEnd = toMinutes(startDayHour);

  for (const course of sorted) {
    const start = toMinutes(course.startTime);
    if (start > prevEnd) {
      intervals.push({
        startTime: toHourStr(prevEnd),
        endTime: toHourStr(start)
      });
    }
    prevEnd = Math.max(prevEnd, toMinutes(course.endTime));
  }

  // Ajouter l'intervalle final si besoin
  const dayEnd = toMinutes(endDayHour);
  if (prevEnd < dayEnd) {
    intervals.push({
      startTime: toHourStr(prevEnd),
      endTime: toHourStr(dayEnd)
    });
  }

  return intervals;
}

/**
 * Ajoute des heures et/ou des minutes à une heure au format "8h00"
 * @param {string} hour - Heure de départ (ex: "8h00" ou "8h")
 * @param {number} hourToAdd - Nombre d'heures à ajouter
 * @param {number} minutesToAdd - Nombre de minutes à ajouter (optionnel)
 * @returns {string} Heure formatée "hhhmm"
 */
export function addHourToString(hour, hourToAdd = 0, minutesToAdd = 0) {
  let [h, m] = hour.split('h');
  h = parseInt(h, 10);
  m = m ? parseInt(m, 10) : 0;

  let totalMinutes = h * 60 + m + hourToAdd * 60 + minutesToAdd;
  let finalH = Math.floor(totalMinutes / 60);
  let finalM = totalMinutes % 60;

  // Pour rester sur 24h, tu peux faire finalH = finalH % 24;
  return `${finalH}h${finalM.toString().padStart(2, '0')}`;
}

/**
 * Filtre un tableau de dates (format "YYYY-MM-DD") sur un mois donné (objet Date ou numéro de mois)
 * @param {string[]} dates - Tableau de dates
 * @param {Date} monthDate - Objet Date représentant le mois à filtrer
 * @returns {string[]} - Dates du mois
 */
export function filterDatesByMonth(dates, monthDate) {
  const month = monthDate.getMonth(); // 0-based
  const year = monthDate.getFullYear();
  return dates.filter(dateStr => {
    const date = new Date(dateStr);
    return date.getMonth() === month && date.getFullYear() === year;
  });
}

export function getHourFromY(clientY, containerTop, pxPerHour = 100, referenceHour = 8) {
  const minutesFromTop = (clientY - containerTop) * 60 / pxPerHour;
  const hour = Math.floor(referenceHour + minutesFromTop / 60);
  const minute = Math.round(minutesFromTop % 60);
  // Formate en "9h05"
  return `${hour}h${minute.toString().padStart(2, '0')}`;
}

/**
 * Retourne l'objet avec la date la plus proche >= referenceDate
 * @param {Array} arr - Ton tableau d'objets (avec champ "date")
 * @param {string} referenceDate - Date de référence au format "YYYY-MM-DD"
 * @returns {Object|null} - L'objet avec la date la plus proche, ou null si aucune date future
 */
export function getNextClosestDate(arr, referenceDate) {
  const ref = new Date(referenceDate);

  // Filtrer les dates >= referenceDate
  const futureDates = arr
    .filter(item => new Date(item.date) >= ref)
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  return futureDates.length ? futureDates[0] : null;
}


